<!--
  @component TopologyIO — P1 control-plane lens.
  chain:   ctx.deployment.control.{topology,validateTopology,diffTopology} → this → ctx.topology
  reads:   the parent node's control surface (browser-safe; pure)
  effects: NONE — read/plan only
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { TOPOLOGY_IO_KEY } from "./deployment-io.js";
  import type { TopologyIOContext } from "./deployment-io.js";

  // Re-export the contract from local bindings (principle 4 —
  // never re-declare).
  export { TOPOLOGY_IO_KEY };
  export type { TopologyIOContext };

  export function useTopologyIO(): TopologyIOContext {
    const ctx = getContext<TopologyIOContext | undefined>(TOPOLOGY_IO_KEY);
    if (!ctx) {
      throw new Error(
        "useTopologyIO() called outside of a TopologyIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import { validateTopology, diffTopology } from "./deployment-io.js";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  const ctx: TopologyIOContext = {
    get ready() {
      return parent.ready && parent.control !== null;
    },
    get topology() {
      return parent.control?.topology ?? null;
    },
    validate: validateTopology,
    diff: diffTopology,
  } satisfies TopologyIOContext;

  setContext(TOPOLOGY_IO_KEY, ctx);
</script>

{@render children?.()}
